import { useMemo, useSyncExternalStore, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FingerPrintIcon, CodeBracketIcon, CloudArrowDownIcon } from '@heroicons/react/24/outline';
import { FoundationsSection } from '@/components/AboutDISGENET/FoundationsSection';
import useText from '@/hooks/useText';
import { useAccessTypes } from '@/components/AboutDISGENET/helpers';
import { ScrollyStaticMobileSection } from './ScrollyStaticMobileSection';
import { StickyDnaScrollSection } from './StickyDnaScrollSection';
import type { StickyDnaStep } from './StickyDnaScrollSection';
import {
  StickyAboutScrollSection,
  ValuePropositionMobileSection,
  type ValuePillarCard,
} from './StickyAboutScrollSection';
import './scrollytelling.css';

const dnaColorVars = {
  '--dna-magenta': 'rgb(209, 0, 145)',
  '--dna-navy': 'rgb(13, 23, 96)',
  '--dna-gray': 'rgb(192, 195, 203)',
} as CSSProperties;

const SCROLLY_DESKTOP_MQ = '(min-width: 860px)';

function useDesktopScrollyLayout() {
  return useSyncExternalStore(
    onStoreChange => {
      const mq = window.matchMedia(SCROLLY_DESKTOP_MQ);
      mq.addEventListener('change', onStoreChange);
      return () => mq.removeEventListener('change', onStoreChange);
    },
    () => window.matchMedia(SCROLLY_DESKTOP_MQ).matches,
    () => true,
  );
}

function AboutScrollytelling() {
  const { t } = useTranslation();
  const heroTitle = useText('about_disgenet.scrolly.hero_title');
  const heroIntro = useText('about_disgenet.scrolly.hero_intro');

  const s3t = useText('about_disgenet.scrolly.sticky_3_title');
  const s4t = useText('about_disgenet.scrolly.sticky_4_title');

  const stickySteps: StickyDnaStep[] = [
    { id: 's3', title: s3t },
    { id: 's4', title: s4t },
  ];

  const valueSectionTitle = t('about_disgenet.value_proposition.section_title');
  const valueCards: ValuePillarCard[] = useMemo(
    () => [
      {
        id: 'drug_rd',
        title: t('about_disgenet.value_proposition.drug_rd.title'),
        tagline: t('about_disgenet.value_proposition.drug_rd.tagline'),
        audienceBadge: t('about_disgenet.value_proposition.drug_rd.audience_badge'),
        bullets: t('about_disgenet.value_proposition.drug_rd.bullets', { returnObjects: true }) as string[],
      },
      {
        id: 'clinical',
        title: t('about_disgenet.value_proposition.clinical.title'),
        tagline: t('about_disgenet.value_proposition.clinical.tagline'),
        audienceBadge: t('about_disgenet.value_proposition.clinical.audience_badge'),
        bullets: t('about_disgenet.value_proposition.clinical.bullets', { returnObjects: true }) as string[],
      },
      {
        id: 'ai_ml',
        title: t('about_disgenet.value_proposition.ai_ml.title'),
        tagline: t('about_disgenet.value_proposition.ai_ml.tagline'),
        audienceBadge: t('about_disgenet.value_proposition.ai_ml.audience_badge'),
        bullets: t('about_disgenet.value_proposition.ai_ml.bullets', { returnObjects: true }) as string[],
      },
      {
        id: 'research',
        title: t('about_disgenet.value_proposition.research.title'),
        tagline: t('about_disgenet.value_proposition.research.tagline'),
        audienceBadge: t('about_disgenet.value_proposition.research.audience_badge'),
        bullets: t('about_disgenet.value_proposition.research.bullets', { returnObjects: true }) as string[],
      },
    ],
    [t],
  );

  const sourcesSectionTitle = useText('about_disgenet.scrolly.sources_section_title');
  const src1b = useText('about_disgenet.scrolly.source_card_1_body');
  const src2b = useText('about_disgenet.scrolly.source_card_2_body');
  const src3b = useText('about_disgenet.scrolly.source_card_3_body');
  const src4b = useText('about_disgenet.scrolly.source_card_4_body');
  const dGenomic = useText('about_disgenet.hero.diagram.genomic');
  const dBiobanks = useText('about_disgenet.hero.diagram.biobanks');
  const dTrials = useText('about_disgenet.hero.diagram.trials');
  const dLiterature = useText('about_disgenet.hero.diagram.literature');

  const hintEyebrow = useText('about_disgenet.scrolly.hint_section_eyebrow');
  const hintMainText = useText('about_disgenet.hero.hint');
  const docHintPrefix = useText('about_disgenet.hero.documentation_hint_prefix');
  const docHintLink = useText('about_disgenet.hero.documentation_hint_link');
  const docHintSuffix = useText('about_disgenet.hero.documentation_hint_suffix');
  const toolsHintPrefix = useText('about_disgenet.scrolly.tools_hint_prefix');
  const toolsHintLink = useText('about_disgenet.scrolly.tools_hint_link');

  const platformsEyebrow = useText('about_disgenet.scrolly.platforms_section_eyebrow');
  const powerTitle = useText('about_disgenet.use_cases.section_title');
  const powerSubtitle = useText('about_disgenet.use_cases.section_subtitle');
  const fitPrefix = useText('about_disgenet.use_cases.fit_prefix');
  const fitHighlight = useText('about_disgenet.use_cases.fit_highlight');
  const fitSuffix = useText('about_disgenet.use_cases.fit_suffix');

  const desktopScrolly = useDesktopScrollyLayout();

  const accessTypes = useAccessTypes();
  const platforms = accessTypes.map(a => ({
    id: String(a.id),
    title: a.title,
    description: a.description,
    hint: a.hint,
    icon: a.icon,
  }));

  const extraBubbles = [
    {
      id: 'extra1',
      title: 'Install',
      description: 'R Package',
      hint: 'Explore DISGENET',
      icon: (
        <CodeBracketIcon style={{ width: '1.875rem', height: '1.875rem', color: 'var(--dna-magenta)' }} />
      ),
    },
    {
      id: 'extra2',
      title: 'Download',
      description: 'Cytoscape',
      hint: 'Analize the data',
      icon: (
        <CloudArrowDownIcon style={{ width: '1.875rem', height: '1.875rem', color: 'var(--dna-magenta)' }} />
      ),
    },
    {
      id: 'extra3',
      title: 'Connect',
      description: 'MCP',
      hint: 'AI agents',
      icon: (
        <img
          src="/static/images/icons/iconsPink/062.svg"
          alt=""
          style={{ width: '1.875rem', height: '1.875rem' }}
        />
      ),
    },
  ];

  const sourceCards = [
    { id: 'src1', title: dGenomic, body: src1b },
    { id: 'src2', title: dBiobanks, body: src2b },
    { id: 'src3', title: dTrials, body: src3b },
    { id: 'src4', title: dLiterature, body: src4b },
  ];

  const dnaScrollyProps = {
    steps: stickySteps,
    sourcesSectionTitle,
    sourceCards,
    hintEyebrow,
    hintMainText,
    hintLeadIcon: <FingerPrintIcon className="scrolly-s3-fingerprint-icon" />,
    hintDocNote: (
      <>
        {docHintPrefix}
        <Link to="/docs" className="scrolly-s3-doc-link">
          {docHintLink}
        </Link>
        {docHintSuffix}
      </>
    ),
    platformsEyebrow,
    powerTitle,
    powerSubtitle,
    fitPrefix,
    fitHighlight,
    fitSuffix,
    toolsLink: (
      <>
        {toolsHintPrefix}{' '}
        <Link to="/tools" className="scrolly-s3-doc-link">
          {toolsHintLink}
        </Link>
      </>
    ),
    platforms,
    extraBubbles,
    overview: <FoundationsSection embedInScrolly />,
    sectionVh: 1920,
  };

  return (
    <article className="about-page about-page--scrolly" style={dnaColorVars}>
      <header className="scrolly-hero">
        <div className="scrolly-hero__content">
          <p className="about-page__eyebrow scrolly-hero__eyebrow">DISGENET</p>
          <h1 className="scrolly-hero__title">{heroTitle}</h1>
          <p className="scrolly-hero__intro">{heroIntro}</p>
          <div className="scrolly-hero__scroll-hint" aria-hidden>
            <span className="scrolly-hero__scroll-line" />
            <span className="scrolly-hero__scroll-label">scroll</span>
          </div>
        </div>
      </header>

      <div className="about-page__shell about-page__shell--scrolly" suppressHydrationWarning>
        {desktopScrolly ? (
          <StickyAboutScrollSection sectionTitle={valueSectionTitle} cards={valueCards} sectionVh={480} />
        ) : (
          <ValuePropositionMobileSection sectionTitle={valueSectionTitle} cards={valueCards} />
        )}
      </div>

      <div className="about-page__shell about-page__shell--scrolly" suppressHydrationWarning>
        {desktopScrolly ? (
          <StickyDnaScrollSection {...dnaScrollyProps} />
        ) : (
          <ScrollyStaticMobileSection {...dnaScrollyProps} />
        )}
      </div>
    </article>
  );
}

export default AboutScrollytelling;
